import JSZip from "jszip";
import { sumBy } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";

import { ApplicantsExamsFilesResponse } from "@api/applicants";

import { FileList } from "@shared/services/fileUpload";
import Message from "@shared/services/message";
import { formatBytes } from "@shared/services/string";
import { fileUploadValidator } from "@shared/services/validation";

export interface UseFileUploadParams {
  initialFileList?: ApplicantsExamsFilesResponse[];
  initialRemainingSize?: number; // remaining size in bytes
  initialRemainingLimit?: number;
}

export function useFileUpload({
  initialFileList = [],
  initialRemainingSize = 0,
  initialRemainingLimit = 0,
}: UseFileUploadParams) {
  const [fileList, setFileList] = useState<FileList[]>([]);
  const [isReadingFiles, setIsReadingFiles] = useState(false);
  const [modifiedZip, setModifiedZip] = useState<string[]>([]);
  const [invalidFiles, setInvalidFiles] = useState<File[]>([]);

  const initialFileListSize = useMemo(
    () => sumBy(initialFileList, "sizeInBytes"),
    [initialFileList],
  );
  const fileNameMap = useMemo(() => {
    const filenames = {};

    initialFileList?.forEach(({ filename, sizeInBytes }) => {
      filenames[filename] = sizeInBytes;
    });
    fileList.forEach(({ file }) => {
      filenames[file.name] = file.size;
    });

    return filenames;
  }, [fileList, initialFileList]);
  const remainingSize = useMemo(
    () =>
      initialRemainingSize -
      initialFileListSize -
      sumBy(fileList, ({ file }) => file.size),
    [fileList, initialFileListSize, initialRemainingSize],
  );
  // Handle duplicate file count
  const fileCount = Math.max(
    initialRemainingLimit - initialFileList?.length - fileList.length,
    0,
  );

  const { acceptedFiles, fileRejections, getRootProps, getInputProps } =
    useDropzone({
      validator: fileUploadValidator({
        fileNameMap,
        maxSize: remainingSize,
        maxFiles: fileCount,
      }),
    });
  const rejectFiles = useMemo(() => {
    const formatMessage = (file: File, id: string) =>
      `${file.name}(${formatBytes(file.size)}): ${Message.getMessageByKey(id)}`;
    const normalizedFileRejections = fileRejections.map(({ errors, file }) => ({
      name: file.name,
      message: formatMessage(file, errors[0].message),
    }));
    const normalizedInvalidFiles = invalidFiles.map((file) => ({
      name: file.name,
      message: formatMessage(file, "uploadFilesModal.invalidFileType"),
    }));
    return [...normalizedFileRejections, ...normalizedInvalidFiles];
  }, [fileRejections, invalidFiles]);

  const removeNodeModules = useCallback(async (file: File) => {
    if (!file.name.endsWith(".zip")) {
      return file;
    }

    const zip = new JSZip();
    let zipFiles;

    try {
      zipFiles = await zip.loadAsync(file);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("Encrypted")) {
          return file;
        }
      }

      // Default to invalid file if the zip file is corrupted
      setInvalidFiles((prev) => [...prev, file]);
      return null;
    }

    const modulesPath = zip.folder(/.*node_modules/)[0]?.name;

    if (!modulesPath) {
      return file;
    }

    zipFiles.remove(modulesPath);

    const newZip = new File(
      [await zipFiles.generateAsync({ type: "blob" })],
      file.name,
    );
    const message = `${newZip.name}(${formatBytes(
      newZip.size,
    )}): ${Message.getMessageByKey("uploadFilesModal.removedNodeModules")}`;
    setModifiedZip((prev) => [...prev, message]);

    return newZip;
  }, []);

  const updateAcceptedFiles = useCallback(async () => {
    const normalizedFiles = await Promise.all(
      acceptedFiles.map(removeNodeModules),
    );
    const newFileList = normalizedFiles.reduce((acc, file) => {
      if (file) {
        acc.push({
          file,
          formatSize: formatBytes(file.size),
        });
      }
      return acc;
    }, [] as FileList[]);
    // When the item is duplicated, the newer file will replace the older one.
    setFileList((prev) =>
      Array.from(
        new Map(
          [...prev, ...newFileList].map((item) => [item.file.name, item]),
        ).values(),
      ),
    );
    setIsReadingFiles(false);
  }, [acceptedFiles, removeNodeModules]);

  const removeFile = (index: number) => {
    const { file } = fileList[index];

    setFileList((prev) =>
      prev.filter(({ file: { name } }) => name !== file.name),
    );
    setInvalidFiles([]);
    setModifiedZip([]);
  };

  useEffect(() => {
    setInvalidFiles([]);
    setModifiedZip([]);

    if (!acceptedFiles.length) {
      return;
    }
    setIsReadingFiles(true);
    updateAcceptedFiles();
  }, [acceptedFiles.length, updateAcceptedFiles]);

  return {
    acceptedFiles,
    fileCount,
    fileList,
    fileRejections,
    isReadingFiles,
    modifiedZip,
    rejectFiles,
    remainingSize,
    getInputProps,
    getRootProps,
    removeFile,
  };
}
